/**
 * 백엔드 API 클라이언트 — **실경로 직호출**(별칭 계층 없음).
 *
 * 구 Nuxt 앱은 `/api/chat` 같은 BFF 별칭을 거쳤다. 그 계층을 다시 만들지 않는다(F §4.3):
 * 별칭은 이름을 하나 더 만들 뿐이고, 그 이름이 계약과 어긋나는 순간 어느 쪽이 진실인지
 * 물어볼 곳이 없어진다. 여기서는 `06-api-spec` 에 적힌 경로를 그대로 쓴다.
 *
 * **인증**: 세션 쿠키(HttpOnly)를 브라우저가 자동으로 싣는다 → `credentials: 'same-origin'`.
 * 토큰을 JS 가 들고 있지 않으므로 XSS 로 새어나갈 자격증명이 없다(백엔드가 쿠키를 소유).
 *
 * **401 처리**: 여기서 리다이렉트하지 않는다. 라우팅은 라우터의 일이고, 이 모듈이 화면을
 * 옮기기 시작하면 테스트가 브라우저 없이는 불가능해진다. 대신 `UnauthorizedError` 를 던지고
 * 상위(쿼리 클라이언트·라우트 가드)가 판단한다.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** 세션 만료·미인증. 라우트 가드가 이 타입만 보고 로그인으로 보낸다. */
export class UnauthorizedError extends ApiError {
  constructor(message = '세션이 만료되었습니다. 다시 로그인해 주세요.') {
    super(401, message)
    this.name = 'UnauthorizedError'
  }
}

type Json = Record<string, unknown>

/** 백엔드 오류 본문에서 사람이 읽을 사유를 뽑는다 — 사유 없는 "실패"만 띄우지 않는다(BFF L7 이관). */
async function detailOf(response: Response): Promise<{ message: string; detail?: unknown }> {
  try {
    const body = (await response.json()) as { detail?: unknown }
    const detail = body?.detail
    if (typeof detail === 'string' && detail) return { message: detail, detail }
    return { message: `요청이 실패했습니다 (HTTP ${response.status})`, detail }
  } catch {
    return { message: `요청이 실패했습니다 (HTTP ${response.status})` }
  }
}

async function raise(response: Response): Promise<never> {
  if (response.status === 401) throw new UnauthorizedError()
  const { message, detail } = await detailOf(response)
  throw new ApiError(response.status, message, detail)
}

export interface RequestOptions {
  method?: string
  body?: Json | FormData
  signal?: AbortSignal
}

/** 공통 요청 — same-origin 쿠키 동반. 성공 시 JSON 파싱, 실패 시 ApiError/UnauthorizedError. */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options
  const isForm = body instanceof FormData
  // exactOptionalPropertyTypes: 없는 값은 `undefined` 를 넣는 게 아니라 **키 자체를 넣지 않는다**.
  // multipart 는 boundary 를 fetch 가 정하므로 content-type 을 직접 넣으면 깨진다.
  const response = await fetch(path, {
    method,
    credentials: 'same-origin',
    ...(isForm || body === undefined
      ? {}
      : { headers: { 'content-type': 'application/json' } }),
    ...(body === undefined ? {} : { body: isForm ? body : JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  })
  if (!response.ok) await raise(response)
  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/** SSE 스트림 요청 — 본문을 파싱하지 않고 `Response` 를 그대로 준다(파서는 F-2 리듀서 소관). */
export async function requestStream(
  path: string,
  body: Json,
  signal?: AbortSignal,
): Promise<Response> {
  const response = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json', accept: 'text/event-stream' },
    body: JSON.stringify(body),
    ...(signal ? { signal } : {}),
  })
  if (!response.ok || !response.body) await raise(response)
  return response
}

/**
 * 파일 응답인가 — **`content-disposition` 유무로만** 가른다.
 *
 * 마스터플랜 xlsx 는 같은 URL·같은 200 이 **파일이거나 JSON**이다(3분기). 구조에서는 이 판별이
 * Nitro 안에 있었고, React 전환은 그것을 브라우저로 **이관**할 뿐 해소하지 않는다(D10 · F §4.1).
 * 판별 규칙을 여기 한 곳에 두는 이유가 그것이다 — 호출부마다 헤더를 다시 들여다보면 그중
 * 하나가 반드시 어긋난다. 계약 고정: `tests/integration/test_frontend_contract_characterization.py`.
 */
export function isFileResponse(response: Response): boolean {
  return Boolean(response.headers.get('content-disposition'))
}

/** 다운로드 — 파일이면 Blob, 미완성/오류면 JSON 을 돌려준다(호출부가 3분기를 그대로 표현). */
export async function requestDownload(
  path: string,
): Promise<{ kind: 'file'; blob: Blob; filename: string } | { kind: 'json'; body: Json }> {
  const response = await fetch(path, { credentials: 'same-origin' })
  if (!response.ok) await raise(response)
  if (!isFileResponse(response)) return { kind: 'json', body: (await response.json()) as Json }
  const disposition = response.headers.get('content-disposition') ?? ''
  const match = /filename="?([^"]+)"?/.exec(disposition)
  return {
    kind: 'file',
    blob: await response.blob(),
    filename: match?.[1] ?? 'download.xlsx',
  }
}
